import { type ClassValue, clsx } from 'clsx';
import { format as fnsFormat, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Currency formatting utility
export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const formatAmount = (amount: string, currencyCode: string = 'USD'): string => {
  const numAmount = parseFloat(amount);
  if (Number.isNaN(numAmount)) return amount;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

// ---------------------------------------------------------------------------
// Date / time formatting utilities
//
// All functions accept a UTC timestamp (ISO 8601 string, Date, or epoch ms)
// and display it in the user's local timezone via date-fns + date-fns-tz.
// ---------------------------------------------------------------------------

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function toLocal(value: string | number | Date): Date {
  const utc = typeof value === 'string' || typeof value === 'number' ? new Date(value) : value;
  if (Number.isNaN(utc.getTime())) throw new Error('Invalid date');
  return toZonedTime(utc, userTimeZone);
}

/** "Mar 6, 2026" */
export const formatDate = (value: string | number | Date): string => {
  try {
    return fnsFormat(toLocal(value), 'MMM d, yyyy');
  } catch {
    return '—';
  }
};

/** "Mar 6, 2026, 2:30 PM" */
export const formatDateTime = (value: string | number | Date): string => {
  try {
    return fnsFormat(toLocal(value), 'MMM d, yyyy, h:mm a');
  } catch {
    return '—';
  }
};

/** "2:30 PM" */
export const formatTime = (value: string | number | Date): string => {
  try {
    return fnsFormat(toLocal(value), 'h:mm a');
  } catch {
    return '—';
  }
};

/** "March 6, 2026" */
export const formatDateLong = (value: string | number | Date): string => {
  try {
    return fnsFormat(toLocal(value), 'MMMM d, yyyy');
  } catch {
    return '—';
  }
};

/** "Mar 2026" – useful for chart axis labels */
export const formatMonthYear = (value: string | number | Date): string => {
  try {
    return fnsFormat(toLocal(value), 'MMM yyyy');
  } catch {
    return '—';
  }
};

/** "Mar 6" – useful for chart tooltip labels */
export const formatMonthDay = (value: string | number | Date): string => {
  try {
    return fnsFormat(toLocal(value), 'MMM d');
  } catch {
    return '—';
  }
};

// ---------------------------------------------------------------------------
// Calendar-date formatting (date-only values with NO timezone shift)
//
// Use for DB `date` columns (execution_date_requested, item_due_date, etc.)
// that represent a calendar day, not an instant in time.
// ---------------------------------------------------------------------------

function toCalendarDate(value: string | Date): Date {
  const str =
    value instanceof Date ? value.toISOString().split('T')[0] : String(value).split('T')[0];
  const parsed = parseISO(str);
  if (Number.isNaN(parsed.getTime())) throw new Error('Invalid date');
  return parsed;
}

/** "Mar 15, 2026" -- no timezone shift; treats the value as a plain calendar date. */
export const formatCalendarDate = (value: string | Date): string => {
  try {
    return fnsFormat(toCalendarDate(value), 'MMM d, yyyy');
  } catch {
    return '—';
  }
};

/** Convert a local Date (e.g. from a date-picker) to a UTC ISO 8601 string for the API. */
export const toUTCISO = (date: Date): string => date.toISOString();

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
};
